import { rankWith, uiTypeIs } from '@jsonforms/core';
import { withJsonFormsControlProps } from '@jsonforms/react';
import { get } from 'lodash';
import { useEffect, useState } from 'react';
import { FormItem } from '../../../../../controls/FormItem';
import {
  SelectWithFilter,
  Option,
} from '../../../../../controls/SelectWithFilter';
import { Tooltip } from '../../../../Tooltip';
import { useDynamicFormContext } from '../../../DynamicFormContext';
import { FieldProps, WithDynamicFormContext } from '../WithDynamicFormContext';
import './style.scss';

type CustomizedInvokeResponse = {
  listItem: any[];
  restOfData?: any;
};

const DependentDropdown = ({
  path,
  label,
  schema,
  handleChange,
  handleRemove,
  handleBlur,
  data,
  required,
  uischema,
  disabled,
  formData,
  optionalMessage,
}: FieldProps) => {
  const { useInvokeAction } = useDynamicFormContext();
  const invokeAction = useInvokeAction?.();
  const [responseData, setResponseData] = useState<CustomizedInvokeResponse>();
  const [searchValue, setSearchValue] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);
  const defaultValue = loading ? 'Loading...' : `Select a ${label}`;

  const listItems = responseData?.listItem;
  const actionData = uischema.options?.actionData;
  const dependentDataPath = uischema.options?.dependencies;
  const dependentPath = uischema.options?.dependentPath;
  const dependentValue = get(formData, dependentPath);
  const dependentStopValue = get(formData, uischema.options?.dependentStopPath);
  const removedPaths = uischema.options?.removedPaths;
  const hiddenFieldPaths = uischema.options?.hiddenFieldPaths;
  const dependentObj = dependentDataPath
    ? Object.fromEntries(
      dependentDataPath?.map((item: any) => [
        item?.key,
        get(formData, item?.path),
      ]),
    )
    : null;

  const isDependentValue = () => {
    const filteredDependentDataPaths = dependentDataPath?.filter(
      (item: any) => item?.optional !== true,
    );
    const arrOfDependentValues = Object.values(
      filteredDependentDataPaths?.map((item: any) => {
        return get(formData, item?.path);
      }),
    );

    return !arrOfDependentValues.includes(undefined);
  };

  const handleInvokeAction = async (data: any, paginate?: boolean) => {
    const invokeResponse: any = (await invokeAction?.({
      actionKey: actionData.actionKey,
      data: data,
    }).catch(() => {
      setResponseData({
        listItem: [],
        restOfData: undefined,
      });
      setLoading(false);
    }))!;
    if (paginate) {
      setResponseData({
        listItem: [...responseData?.listItem!, ...invokeResponse?.data?.value],
        restOfData: invokeResponse.data,
      });
    } else {
      setResponseData({
        listItem: invokeResponse?.data?.value || [],
        restOfData: invokeResponse?.data,
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    if (dependentStopValue) {
      setResponseData({
        listItem: [],
        restOfData: undefined,
      });
      return;
    }
    if (isDependentValue()) {
      setLoading(true);
      handleInvokeAction({
        ...dependentObj,
        ...actionData.data,
        options: {
          paginate: {
            limit: 10,
            offset: 0,
          },
        },
      });
    } else {
      setResponseData({
        listItem: [],
        restOfData: undefined,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dependentValue]);

  useEffect(() => {
    if (dependentStopValue) return;
    const timeout = setTimeout(() => {
      handleOnSearch();
    }, 200);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);

  const labelPath = uischema.options?.labelPath;
  const selectedValue = get(formData, labelPath);

  const handleOnChange = (value: any) => {
    if (schema.type === 'number') {
      handleChange(path, +value);
      handleOnSelect(+value);
    } else {
      if (!!value) {
        handleChange(path, value.toString());
        handleOnSelect(value.toString());
      } else {
        handleRemove(path);
      }
    }
  };

  const handleOnSelect = (value: any) => {
    removedPaths.forEach((removedPath: string) => {
      handleRemove(removedPath);
    });
    if (hiddenFieldPaths) {
      invokeAction?.({
        actionKey: actionData.actionKey,
        data: { ...dependentObj, [actionData.data.type]: value },
      }).then((data: any) => {
        const fullData = data?.data?.value;
        hiddenFieldPaths.forEach((field: any) => {
          handleChange(field.path, fullData?.[field.value]);
        });
      });
    }
  };

  const handleOnSearch = () => {
    if (isDependentValue()) {
      handleInvokeAction({
        ...dependentObj,
        ...actionData.data,
        options: {
          filter: { keyword: searchValue },
          paginate: {
            limit: 10,
            offset: 0,
          },
        },
      });
    }
  };

  const handleOnScroll = () => {
    if (listItems?.length === responseData?.restOfData?.paging?.count) return;
    handleInvokeAction(
      {
        ...dependentObj,
        ...actionData.data,
        options: {
          filter: { keyword: searchValue },
          paginate: {
            limit: responseData?.restOfData?.paging?.limit,
            offset: 10 + responseData?.restOfData?.paging?.offset,
          },
        },
      },
      true,
    );
  };

  return (
    <FormItem
      label={label as string}
      required={required}
      classes="tru-select-input-control"
      optionalMessage={optionalMessage}
    >
      <div className="select-with-filter-container">
        <Tooltip
          classes={selectedValue?.length! > 35 ? '' : 'unset'}
          title={selectedValue}
          position="top right"
        >
          <SelectWithFilter
            onBlur={() => handleBlur(path)}
            value={selectedValue || data || defaultValue}
            disabled={disabled}
            onChange={handleOnChange}
            onSearch={setSearchValue}
            onPopupScroll={handleOnScroll}
            onDropdownVisibleChange={open => {
              if (!open) setSearchValue(undefined)
            }}
          >
            {loading ? (
              <Option>
                <span>Loading...</span>
              </Option>
            ) : (
              listItems?.map((opt: any) => (
                <Option
                  key={`select-option-${opt?.const || opt}`}
                  value={opt?.const || opt}
                  title={opt?.title || opt}
                >
                  <span>{opt?.title || opt}</span>
                </Option>
              ))
            )}
          </SelectWithFilter>
        </Tooltip>
        {schema.description && (
          <p className="description margin-bottom">{schema.description}</p>
        )}
      </div>
    </FormItem>
  );
};

export const DependentDropdownEntry = {
  tester: rankWith(12, uiTypeIs('DependentDropdown')),
  renderer: withJsonFormsControlProps(
    WithDynamicFormContext(DependentDropdown),
  ),
};
